import type {
  SurveyAnswer,
  SurveyAnswers,
  SurveyCondition,
  SurveyDefinition,
  SurveyIssue,
  SurveyIssueCode,
  SurveyQuestion,
} from '../types/survey';

/**
 * The rules a survey definition sets for its answers: which questions the earlier answers open,
 * what is dropped, and what is wrong. The API enforces them and the admin form runs the same
 * checks, so a response fails before the round trip rather than after it.
 *
 * The backend loads this file at runtime, so it must not import anything but types.
 */

export const SURVEY_TEXT_MAX_LENGTH = 200;
export const SURVEY_TEXTAREA_MAX_LENGTH = 1_000;
export const SURVEY_PHONE_MAX_LENGTH = 30;
export const SURVEY_PHONE_MIN_DIGITS = 7;

/** The longest a typed answer to this question may be. */
export function surveyMaxLength(question: SurveyQuestion): number {
  if (question.maxLength) return question.maxLength;
  if (question.type === 'textarea') return SURVEY_TEXTAREA_MAX_LENGTH;
  if (question.type === 'phone') return SURVEY_PHONE_MAX_LENGTH;
  return SURVEY_TEXT_MAX_LENGTH;
}

/** Answered by picking from `options` rather than by typing. */
export const isChoiceQuestion = (question: SurveyQuestion) =>
  question.type === 'single' || question.type === 'multiple';

/** A question as it is asked: with its group's condition, if any, ahead of its own. */
export interface AskedQuestion {
  question: SurveyQuestion;
  /** Every one must hold for the question to be asked. */
  conditions: SurveyCondition[];
}

/** Every question of a survey, in the order it is asked. */
export function surveyQuestions(definition: SurveyDefinition): AskedQuestion[] {
  const asked: AskedQuestion[] = [];
  const conditionsOf = (...conditions: (SurveyCondition | undefined)[]) =>
    conditions.filter((condition): condition is SurveyCondition => condition !== undefined);
  for (const section of definition.sections) {
    for (const item of section.items) {
      if (item.kind === 'question') {
        asked.push({ question: item, conditions: conditionsOf(item.showIf) });
      } else {
        for (const question of item.questions) {
          asked.push({ question, conditions: conditionsOf(item.showIf, question.showIf) });
        }
      }
    }
  }
  return asked;
}

/** An answer by key, never one inherited from `Object.prototype`. */
function answerTo(answers: SurveyAnswers, key: string): SurveyAnswer | undefined {
  return Object.hasOwn(answers, key) ? answers[key] : undefined;
}

/** The codes an answer holds, whether one was picked or several ticked. */
export function answerCodes(answer: SurveyAnswer | undefined): string[] {
  if (answer === undefined || answer === '') return [];
  return Array.isArray(answer) ? answer : [answer];
}

export function conditionHolds(condition: SurveyCondition, answers: SurveyAnswers): boolean {
  return answerCodes(answerTo(answers, condition.question)).some((code) =>
    condition.anyOf.includes(code),
  );
}

/** Text trimmed; a blank or nothing ticked is no answer at all. */
function settled(answer: SurveyAnswer | undefined): SurveyAnswer | undefined {
  if (typeof answer === 'string') return answer.trim() || undefined;
  if (Array.isArray(answer)) return answer.length > 0 ? answer : undefined;
  return answer;
}

/** A `YYYY-MM-DD` that names a real day. */
export function isSurveyDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

const digitCount = (value: string) => value.replace(/\D/g, '').length;

type Problem = { code: SurveyIssueCode; message: string } | null;

function requiredProblem(question: SurveyQuestion): NonNullable<Problem> {
  if (question.type === 'single') return { code: 'required', message: 'Pick an answer' };
  if (question.type === 'multiple') return { code: 'required', message: 'Tick at least one box' };
  return { code: 'required', message: 'Enter an answer' };
}

function answerProblem(question: SurveyQuestion, answer: SurveyAnswer): Problem {
  const options = question.options ?? [];
  const codes = new Set(options.map((option) => option.value));

  if (question.type === 'single') {
    if (typeof answer !== 'string') return { code: 'invalid_type', message: 'Pick one answer' };
    if (!codes.has(answer)) return { code: 'invalid_option', message: 'Not one of the options' };
    return null;
  }

  if (question.type === 'multiple') {
    if (!Array.isArray(answer)) return { code: 'invalid_type', message: 'Send a list of answers' };
    if (answer.some((code) => !codes.has(code))) {
      return { code: 'invalid_option', message: 'Not one of the options' };
    }
    if (new Set(answer).size !== answer.length) {
      return { code: 'duplicate_option', message: 'An option is ticked twice' };
    }
    const lone = options.find((option) => option.exclusive && answer.includes(option.value));
    if (lone && answer.length > 1) {
      return { code: 'exclusive', message: `"${lone.value}" can only be ticked on its own` };
    }
    if (question.maxSelections !== undefined && answer.length > question.maxSelections) {
      return { code: 'too_many', message: `Tick ${question.maxSelections} at most` };
    }
    return null;
  }

  if (typeof answer !== 'string') return { code: 'invalid_type', message: 'Type an answer' };
  const max = surveyMaxLength(question);
  if (answer.length > max) return { code: 'too_long', message: `Keep it to ${max} characters` };
  if (question.type === 'phone' && digitCount(answer) < SURVEY_PHONE_MIN_DIGITS) {
    return { code: 'invalid_phone', message: 'Enter a phone number to call' };
  }
  if (question.type === 'date' && !isSurveyDate(answer)) {
    return { code: 'invalid_date', message: 'Enter a date as YYYY-MM-DD' };
  }
  return null;
}

export interface SurveyCheck {
  /** What is stored: trimmed, blanks left out, and nothing for a question that was not asked. */
  answers: SurveyAnswers;
  /** Empty when the answers may be saved. */
  issues: SurveyIssue[];
}

/**
 * Checks answers against a definition. A key that is not a question is refused; an answer to a
 * question the earlier answers did not open is dropped, not refused, so a form that keeps a
 * branch's answers while it is closed still saves.
 */
export function checkSurveyAnswers(
  definition: SurveyDefinition,
  input: SurveyAnswers,
): SurveyCheck {
  const asked = surveyQuestions(definition);
  const known = new Set(asked.map(({ question }) => question.key));
  const issues: SurveyIssue[] = [];
  const report = (key: string, problem: NonNullable<Problem>) =>
    issues.push({ path: ['answers', key], ...problem });

  for (const key of Object.keys(input)) {
    if (!known.has(key)) {
      report(key, { code: 'unknown_question', message: 'Not a question in this survey' });
    }
  }

  const answers: SurveyAnswers = {};
  for (const { question, conditions } of asked) {
    if (!conditions.every((condition) => conditionHolds(condition, answers))) continue;
    const answer = settled(answerTo(input, question.key));
    if (answer === undefined) {
      if (question.required) report(question.key, requiredProblem(question));
      continue;
    }
    const problem = answerProblem(question, answer);
    if (problem) report(question.key, problem);
    answers[question.key] = answer;
  }
  return { answers, issues };
}

/**
 * The boxes ticked after a click on a `multiple` question. Ticking an exclusive option ("none of
 * these") clears the rest, and ticking anything else clears it.
 */
export function tickSurveyOptions(
  question: SurveyQuestion,
  previous: string[],
  next: string[],
): string[] {
  const exclusive = new Set(
    (question.options ?? []).filter((option) => option.exclusive).map((option) => option.value),
  );
  const added = next.filter((code) => !previous.includes(code));
  const lone = added.find((code) => exclusive.has(code));
  if (lone) return [lone];
  return added.length > 0 ? next.filter((code) => !exclusive.has(code)) : next;
}
